import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Router } from '@angular/router';
import { User } from '../models/auth.models';
import { MatDialog } from '@angular/material/dialog';
import { Apollo } from 'apollo-angular';
import {
  LOGIN,
  MOBILE_LOGIN,
  MOBILE_OTP_LOGIN,
  LOGOUT,
  FORGOT_PASSWORD,
  RESET_PASSWORD,
  CHANGE_PASSWORD,
} from '../schema/mutation/login.mutation';
import { OtpService } from 'src/app/services/otp.service';
import { environment } from 'src/environments/environment';
import {
  cancelScheduledRefresh,
  readStoredUser,
  scheduleAccessRefresh,
} from './token-refresh';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private router: Router,
    private dialog: MatDialog,
    // Injector (not Apollo directly) — breaks the circular dependency:
    //   AuthService → Apollo → APOLLO_OPTIONS factory → inject(AuthService)
    // By deferring Apollo resolution to method-call time, the circle is cut.
    private injector: Injector,
    private otpService: OtpService,
  ) {
    const storedUser = readStoredUser();
    if (storedUser) {
      this.currentUserSubject.next(storedUser);
      this.armRefresh(storedUser);
    }
  }

  // ─── Login Mutations ────────────────────────────────────────────────────────

  login(username: string, password: string, _type?: string): Observable<any> {
    const apollo = this.injector.get(Apollo);
    return apollo.mutate({
      mutation: LOGIN,
      variables: { username, password },
      fetchPolicy: 'no-cache',
    });
  }

  mobileLogin(mobile: string, password: string): Observable<any> {
    const apollo = this.injector.get(Apollo);
    return apollo.mutate({
      mutation: MOBILE_LOGIN,
      variables: { mobile, password },
      fetchPolicy: 'no-cache',
    });
  }

  mobileOtpLogin(mobile: string): Observable<any> {
    const apollo = this.injector.get(Apollo);
    return apollo.mutate({
      mutation: MOBILE_OTP_LOGIN,
      variables: { mobile },
      fetchPolicy: 'no-cache',
    });
  }

  // ─── Session Storage ─────────────────────────────────────────────────────────

  storeSession(user: User): void {
    const previous = readStoredUser();
    const merged: User = { ...previous, ...user };
    if (user.district == null && previous?.district) {
      merged.district = previous.district;
    }
    const sessionStart =
      merged.sessionStart
        ? merged.sessionStart * 1000
        : Date.now();

    localStorage.setItem('loggedInUser', JSON.stringify(merged));
    localStorage.setItem('sessionStart', String(sessionStart));
    this.currentUserSubject.next(merged);
    this.armRefresh(merged);
  }

  isAbsoluteSessionExpired(): boolean {
    return false;
  }

  sessionRemainingMs(): number {
    return Number.POSITIVE_INFINITY;
  }

  setCurrentUser(user: User | null): void {
    this.currentUserSubject.next(user);
  }

  // ─── Logout ───────────────────────────────────────────────────────────────

  logout(): void {
    cancelScheduledRefresh();
    const apollo = this.injector.get(Apollo);
    const refreshToken = readStoredUser()?.refreshToken;
    apollo.mutate({ mutation: LOGOUT, variables: { refreshToken } }).subscribe({
      next: () => {
        this.dialog.closeAll();
        localStorage.clear();
        this.currentUserSubject.next(null);
        this.router.navigate(['/login']);
      },
      error: () => {
        this.dialog.closeAll();
        localStorage.clear();
        this.currentUserSubject.next(null);
        this.router.navigate(['/login']);
      }
    });
  }

  // ─── Auth Check ───────────────────────────────────────────────────────────

  isLoggedIn(): boolean {
    return !!localStorage.getItem('loggedInUser');
  }

  getTokenExpiry(): number {
    return 0; // Tokens are now HttpOnly cookies, so the frontend cannot read the expiry directly.
  }

  isTokenExpired(): boolean {
    return false;
  }

  private armRefresh(user: User): void {
    scheduleAccessRefresh(
      user,
      environment.apiUrl,
      (next) => {
        if (!localStorage.getItem('loggedInUser')) return;
        this.storeSession(next);
      },
      () => this.logout(),
    );
  }

  // ─── Forgot Password / OTP ────────────────────────────────────────────────

  /** Step 1: validate user identity (mobile + username must match) */
  forgotPassword(mobile: string, username: string): Observable<any> {
    const apollo = this.injector.get(Apollo);
    return apollo.mutate({
      mutation: FORGOT_PASSWORD,
      variables: { mobile, username },
      fetchPolicy: 'no-cache',
    });
  }

  /** Step 1b: send OTP to mobile — delegates to OtpService */
  sendOtp(mobile: string): Observable<any> {
    return this.otpService.passwordResetOtp(mobile);
  }

  /** Step 2: verify OTP — delegates to OtpService */
  verifyOtp(phone: string, otp: string): Observable<any> {
    return this.otpService.verifyOtp(phone, otp);
  }

  /** Step 3: set new password after OTP verified. Username picks the exact
   *  account when several member accounts share the mobile number. */
  resetPassword(mobile: string, newPassword: string, username?: string): Observable<any> {
    const apollo = this.injector.get(Apollo);
    return apollo.mutate({
      mutation: RESET_PASSWORD,
      variables: { mobile, newPassword, username },
      fetchPolicy: 'no-cache',
    });
  }

  changePassword(mobile: string, oldPassword: string, newPassword: string): Observable<any> {
    const apollo = this.injector.get(Apollo);
    return apollo.mutate({
      mutation: CHANGE_PASSWORD,
      variables: { mobile, oldPassword, newPassword },
      fetchPolicy: 'no-cache',
    });
  }
}
